import { FilterUUID, WidgetUUID } from '@/common/utils';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  PeriodTimeType,
  DataFieldName,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/variableListWindow/variable.types';
import {
  FilterType,
  FilterData,
} from '@/dashboard/features/widgets/filter/widget/domain/filterData.types';
import { getFilterValue } from './filter-search';

type ClickedData = string | string[] | number | boolean | undefined;

export type ClickedMeta = {
  widgetStartTime?: string;
  widgetEndTime?: string;
};

export interface ClickHistoryItem {
  uuid: string;
  name: string;
  data: ClickedData;
  filterType?: FilterType;
  timeType?: PeriodTimeType;
  meta?: ClickedMeta;
}

export class ClickHistory {
  stack: ClickHistoryItem[] = [];

  readonly MAX_STACK_SIZE = 200;

  add(item: ClickHistoryItem) {
    // 기존 요소 제거
    this.stack = this.stack.filter(
      (el) => !(el.uuid === item.uuid && el.name === item.name && el.timeType === item.timeType),
    );
    // 최신 요소 추가
    this.stack.push(item);
    if (this.stack.length > this.MAX_STACK_SIZE) {
      this.stack.shift();
    }
  }

  findTopMatching(
    info: {
      widgetId: WidgetUUID;
      filterId: FilterUUID | DataFieldName;
      timeType?: PeriodTimeType;
    }[],
  ): ClickHistoryItem | undefined {
    const uuidSet = new Set(
      info.map((item) => `${item.widgetId}--${item.filterId}--${item.timeType}`),
    );
    // 스택의 가장 위에서 아래로 찾기
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (uuidSet.has(`${this.stack[i].uuid}--${this.stack[i].name}--${this.stack[i].timeType}`)) {
        return this.stack[i];
      }
    }
    return undefined;
  }
}

export interface ClickedWidgetInfo {
  filterUUID: FilterUUID;
  value: ClickedData;
  filterType?: FilterType;
  timeType?: PeriodTimeType;
  meta?: ClickedMeta;
}

export const useWidgetClickStore = defineStore('widgetClickStore', () => {
  const clickedWidgetInfo = ref<ClickHistory>(new ClickHistory());

  const setClickedHistoryStack = (stack: ClickHistoryItem[]) => {
    clickedWidgetInfo.value.stack = stack;
  };

  const updateClickedWidgetInfo = (widgetUUID: WidgetUUID, info: ClickedWidgetInfo[]) => {
    info.forEach((value) => {
      clickedWidgetInfo.value.add({
        uuid: widgetUUID,
        name: value.filterUUID,
        data: value.value,
        filterType: value.filterType,
        timeType: value.timeType,
        meta: value.meta,
      });
    });
  };

  const getClickedWidget = (
    info: {
      widgetId: WidgetUUID;
      filterId: FilterUUID | DataFieldName;
      timeType?: PeriodTimeType;
    }[],
  ) => clickedWidgetInfo.value.findTopMatching(info);

  const updateFilterWidgetInfo = (widgetUUID: WidgetUUID, info: Map<FilterUUID, FilterData>) => {
    updateClickedWidgetInfo(
      widgetUUID,
      Array.from(info).reduce<ClickedWidgetInfo[]>((acc, [filterUUID, value]) => {
        if (value.filterType === 'period-calendar') {
          acc.push({
            filterUUID,
            value: getFilterValue(value, 'fromTime'),
            filterType: value.filterType,
            timeType: 'fromTime',
          });
          acc.push({
            filterUUID,
            value: getFilterValue(value, 'toTime'),
            filterType: value.filterType,
            timeType: 'toTime',
          });
        } else {
          acc.push({ filterUUID, value: getFilterValue(value), filterType: value.filterType });
        }
        return acc;
      }, []),
    );
  };

  const route = useRoute();
  const dashboardId = computed(() => route.params.id as string);

  watch(dashboardId, () => {
    clickedWidgetInfo.value.stack = [];
  });

  return {
    clickedWidgetInfo,
    setClickedHistoryStack,
    updateClickedWidgetInfo,
    getClickedWidget,
    updateFilterWidgetInfo,
  };
});
